import { Box, CircularProgress, Stack } from '@mui/material'
import { DynamicFlowStepper, DynamicFlowView, useDynamicFlowController } from '../../DynamicFlow'
import { FC, useEffect, useMemo } from 'react'

import { ActionPage } from '../../ActionPage'
import { AdditionalVisualsStep } from '../AdditionalVisualsStep.component'
import { CreativeUploadWizardSubmitModal } from '../CreativeUploadWizardSubmitModal.component'
import { GRAY_600 } from 'constants/styling/theme'
import { MUIButton } from '../../MUIButton'
import { RequiredVisualsStep } from '../RequiredVisualsStep.component'
import { useCreativeUploadWizard } from './CreativeUploadWizard.context'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

export enum CreativeUploadWizardStep {
  REQUIRED_VISUALS = 'REQUIRED_VISUALS',
  ADDITIONAL_VISUALS = 'ADDITIONAL_VISUALS',
}

/**
 * Navigation buttons for CT upload flow
 * @example <Navigation />
 */
const Navigation: FC = () => {
  const { t } = useTranslation(['ct_upload_wizard', 'common'])
  const flowCtrl = useDynamicFlowController()
  const {
    areVisualsMissing,
    setIsSubmitOpen,
    fileCtrl,
    requiredVisualAmount,
    uploadedRequiredVisualsAmount,
  } = useCreativeUploadWizard()

  const isLast = useMemo(() => !flowCtrl.currentNode?.next, [flowCtrl.currentNode?.next])

  return (
    <Stack
      direction="row"
      justifyContent="flex-end"
      alignItems="center"
      gap={2}
      height="100%"
      p="0 2rem"
    >

      {!!flowCtrl.currentNode?.prev &&
        <MUIButton
          type="secondaryBorder"
          onClick={() => { flowCtrl.setHead(flowCtrl.currentNode!.prev!) }}
          disabled={fileCtrl.isAnyActionRunning}
        >
          {t('common:Previous')}
        </MUIButton>
      }

      {flowCtrl.currentNode?.key === CreativeUploadWizardStep.REQUIRED_VISUALS &&
        <MUIButton
          onClick={() => {
            if (!areVisualsMissing) flowCtrl.setHead(flowCtrl.currentNode!.next!)
            else setIsSubmitOpen(true)
          }}
          disabled={fileCtrl.isAnyActionRunning || !uploadedRequiredVisualsAmount || uploadedRequiredVisualsAmount > requiredVisualAmount}
        >
          {t('common:Next')}
        </MUIButton>
      }

      {isLast &&
        <MUIButton
          onClick={() => setIsSubmitOpen(true)}
          disabled={fileCtrl.isAnyActionRunning}
        >
          {t('submit')}
        </MUIButton>
      }

    </Stack>
  )
}

/**
 * Controller for CT upload flow
 * @example <CreativeUploadWizardController />
 */
export const CreativeUploadWizardController: FC = () => {
  const { t } = useTranslation('ct_upload_wizard')
  const flowCtrl = useDynamicFlowController()
  const navigate = useNavigate()

  const {
    isSubmitOpen,
    assignmentQuery,
    visualsRequirementsQuery,
    setIsSubmitOpen,
  } = useCreativeUploadWizard()

  const isLoading = useMemo(
    () => assignmentQuery.isPending || visualsRequirementsQuery.isPending,
    [assignmentQuery.isPending, visualsRequirementsQuery.isPending]
  )

  useEffect(() => {
    flowCtrl.createEntryNode(
      CreativeUploadWizardStep.REQUIRED_VISUALS,
      {
        component: <RequiredVisualsStep />,
        hasFooter: true,
        type: 'main',
      }
    )

    flowCtrl.insertAfter(
      CreativeUploadWizardStep.REQUIRED_VISUALS,
      CreativeUploadWizardStep.ADDITIONAL_VISUALS,
      {
        component: <AdditionalVisualsStep />,
        hasFooter: true,
        type: 'main',
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ActionPage
      id="ct-upload-wizard"
      title={t('title')}
      customStepper={<DynamicFlowStepper stepNameTranslatorFnc={(key) => t(`stepper.${key}`)} />}
      onClose={() => navigate(-1)}
      footerChildren={<Navigation />}
    >
      <Box width="100%" height="100%" overflow="auto" position="relative">

        {!isLoading &&
          <DynamicFlowView />
        }

        {isLoading &&
          <CircularProgress
            sx={{
              position: 'absolute',
              top: '50%',
              right: 'calc(50% - 25px)',
              color: GRAY_600
            }}
            variant="indeterminate"
            size={50}
          />
        }

        <CreativeUploadWizardSubmitModal isOpen={isSubmitOpen} onClose={() => setIsSubmitOpen(false)} />

      </Box>
    </ActionPage>
  )
}
